// 暦情報ユーティリティ（v1.1）
// - 月齢
// - 月の満ち欠けアイコン・和名
// - 旧暦
// - 潮種別（旧暦日ベース）
// - 曜日

import { Solar } from 'lunar-typescript';

// ── 曜日 ──
const WEEKDAYS_JA = ['日', '月', '火', '水', '木', '金', '土'] as const;

export function weekdayJa(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  return WEEKDAYS_JA[dt.getUTCDay()] ?? '?';
}

// ── 月齢 ──
const SYNODIC_MONTH = 29.530588853;
const NEW_MOON_REF_JD = 2451550.1; // 2000-01-06 18:14 UT 新月

/** "YYYY-MM-DD" (JST) の正午基準で月齢を返す。0〜29.53。 */
export function moonAge(dateStr: string): number {
  const [y, m, d] = dateStr.split('-').map(Number) as [number, number, number];
  const ms = Date.UTC(y, m - 1, d, 3, 0, 0); // JST 正午 = UTC 03:00
  const jd = ms / 86400000 + 2440587.5;
  let age = (((jd - NEW_MOON_REF_JD) % SYNODIC_MONTH) + SYNODIC_MONTH) % SYNODIC_MONTH;
  if (age >= SYNODIC_MONTH) age -= SYNODIC_MONTH;
  return age;
}

// ── 月の満ち欠けアイコン（月齢を反映） ──
/** 月齢から Unicode 月絵文字を返す（8 段階）。 */
export function moonPhaseEmoji(age: number): string {
  // SYNODIC_MONTH を 8 等分して切り替え
  const seg = Math.floor((age / SYNODIC_MONTH) * 8 + 0.5) % 8;
  // 0:新月 1:三日月 2:上弦 3:十三夜 4:満月 5:居待 6:下弦 7:二十六夜
  return ['🌑', '🌒', '🌓', '🌔', '🌕', '🌖', '🌗', '🌘'][seg]!;
}

// ── 月の和名（伝統的な呼称） ──
//
// 月の和名はすべて **旧暦日基準** で名付けられている（三日月＝旧暦3日、十五夜＝旧暦15日 …）。
// 月齢ベースで「四捨五入して N日月」とやると、朔の瞬間が JST 12:00 以降に起きる月で
// 「晦」が連日表示され「新月」が出ない不具合が発生する。そのため旧暦日 + 翌日の旧暦日
// （≒ 月の最終日かどうか）から判定する。

// 内部ヘルパ。state/store.ts にも shiftDate があるが、暦計算側で循環 import を避けるため
// ここに局所定義する。
function shiftDateOneDay(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + 1);
  return dt.toISOString().slice(0, 10);
}

function moonNameFromLunarDay(day: number): string {
  switch (day) {
    case 1: return '新月';
    case 2: return '二日月';
    case 3: return '三日月';
    case 7: case 8: return '上弦の月';
    case 13: return '十三夜月';
    case 14: return '小望月';
    case 15: return '満月';
    case 16: return '十六夜';
    case 17: return '立待月';
    case 18: return '居待月';
    case 19: return '寝待月';
    case 20: return '更待月';
    case 22: case 23: return '下弦の月';
    case 26: return '二十六夜';
    default: return `${day}日月`;
  }
}

/**
 * 西暦日付に対応する月の和名。
 * 翌日の旧暦日が 1 ならその日は旧暦の最終日 → 「晦」。
 * 29日月／30日月の差を吸収するため翌日を見て判定する。
 */
export function moonNameForDate(dateStr: string): string {
  const today = toLunar(dateStr).day;
  const tomorrow = toLunar(shiftDateOneDay(dateStr)).day;
  if (tomorrow === 1) return '晦';
  return moonNameFromLunarDay(today);
}

// ── 旧暦 ──
export interface LunarDate {
  year: number;
  month: number;
  day: number;
  isLeapMonth: boolean;
  display: string;
}

/**
 * 西暦日付 (YYYY-MM-DD JST) → 日本旧暦。
 * lunar-typescript（中国農暦）ベース。近現代の日付では日本旧暦と概ね一致。
 */
export function toLunar(dateStr: string): LunarDate {
  const [y, m, d] = dateStr.split('-').map(Number) as [number, number, number];
  const solar = Solar.fromYmd(y, m, d);
  const lunar = solar.getLunar();
  const month = lunar.getMonth();
  const isLeap = month < 0;
  const monthAbs = Math.abs(month);
  return {
    year: lunar.getYear(),
    month: monthAbs,
    day: lunar.getDay(),
    isLeapMonth: isLeap,
    display: `旧暦 ${isLeap ? '閏' : ''}${monthAbs}月${lunar.getDay()}日`,
  };
}

// ── 潮種別（旧暦日ベース、日本の伝統的な区分） ──
export type TideType = '大潮' | '中潮' | '小潮' | '長潮' | '若潮';

/**
 * 旧暦日（1〜30）から日本の伝統的な潮種別を返す。
 *
 * 日本の漁業で広く使われている分類:
 *   大潮: 1, 2, 14, 15, 16, 17 (朔と望の前後)
 *   中潮: 3, 4, 5, 12, 13, 18, 19, 20, 27, 28, 29, 30
 *   小潮: 6, 7, 8, 21, 22, 23 (上弦と下弦の付近)
 *   長潮: 9, 10, 24, 25
 *   若潮: 11, 26
 */
export function tideTypeFromLunarDay(lunarDay: number): TideType {
  const d = ((lunarDay - 1) % 30) + 1; // 1..30 にクランプ
  switch (d) {
    case 1: case 2: case 14: case 15: case 16: case 17:
      return '大潮';
    case 6: case 7: case 8: case 21: case 22: case 23:
      return '小潮';
    case 9: case 10: case 24: case 25:
      return '長潮';
    case 11: case 26:
      return '若潮';
    default:
      return '中潮';
  }
}
